// CLIENT-SIDE SERVICE
// This service calls the secure server-side proxy at Craig's Classroom.

const AI_PROXY_URL = "/api/secure-ai-proxy.php";
const AI_TIMEOUT_MS = 12000;

const OFFLINE_TIPS: Record<string, string> = {
  'virus-worm': "Offline Tip: A Virus needs a host file (like a document) to run. A Worm is standalone and travels across networks automatically.",
  'trojan': "Offline Tip: Trojans disguise themselves as legitimate software (games, tools). They don't self-replicate; they trick you into installing them.",
  'ransomware': "Offline Tip: The best defence against Ransomware is the 3-2-1 backup rule. If you have offline backups, you don't need to pay.",
  'spyware-keyloggers': "Offline Tip: Keyloggers steal passwords as you type. Multi-Factor Authentication (MFA) defeats this because they can't type your phone code.",
  'rootkits': "Offline Tip: Rootkits hide at the administrator/kernel level. Often, the only way to be safe is to wipe the hard drive and reinstall Windows.",
  'botnets': "Offline Tip: Botnets are armies of infected devices. Always change the default password on your router and smart home devices.",
  'default': "Offline Tip: Remember the defensive basics: Keep software updated (patching), use strong unique passwords, and enable MFA."
};

export const getOfflineTip = (moduleId: string): string => {
  return OFFLINE_TIPS[moduleId] || OFFLINE_TIPS['default'];
};

function looksUnsafe(text: string = "") {
  const t = text.toLowerCase();
  const redFlags = [
    "write malware", "create malware", "build a virus", "make a trojan",
    "ransomware code", "keylogger code", "evade antivirus", "bypass edr",
    "disable defender", "persistence", "payload", "exploit"
  ];
  return redFlags.some(p => t.includes(p));
}

interface AskTutorParams {
  prompt: string;
  context?: string;
  moduleId?: string;
  appToken?: string | null;
}

export interface AskTutorResponse {
  ok: boolean;
  reply?: string;
  error?: string;
  status?: number;
}

export async function askTutor({ prompt, context, moduleId, appToken = null }: AskTutorParams): Promise<AskTutorResponse> {
  if (!prompt || !prompt.trim()) return { ok: false, error: "Please type a question." };

  if (looksUnsafe(prompt)) {
    return {
      ok: false,
      error: "I can’t help with offensive/operational requests. I can explain defence, detection, and response."
    };
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), AI_TIMEOUT_MS);

  const headers: Record<string, string> = { "Content-Type": "application/json" };
  // Only include this if your proxy expects it:
  if (appToken) headers["X-App-Token"] = appToken;

  try {
    const res = await fetch(AI_PROXY_URL, {
      method: "POST",
      headers,
      body: JSON.stringify({
        prompt: prompt.trim(),
        message: prompt.trim(), // compatibility for proxies expecting "message"
        context: String(context || "").slice(0, 4000),
        moduleId: moduleId ?? "unknown"
      }),
      signal: controller.signal
    });

    const contentType = res.headers.get("content-type") || "";
    const data: any = contentType.includes("application/json")
      ? await res.json()
      : { reply: await res.text() };

    if (!res.ok) {
      return { ok: false, status: res.status, error: data?.error || data?.message || `HTTP ${res.status}` };
    }

    const reply = String(
      data?.reply ?? data?.text ?? data?.output ?? data?.content ?? data?.message ?? ""
    ).trim();

    return reply ? { ok: true, reply } : { ok: false, error: "Tutor returned an empty response." };
  } catch (e: any) {
    const msg = e?.name === "AbortError"
      ? "Tutor timed out. Please try again."
      : "Tutor is offline right now — use the offline tips below.";
    return { ok: false, error: msg };
  } finally {
    clearTimeout(timeoutId);
  }
}
